import React from 'react';
import { Form, FormControlProps } from 'react-bootstrap';
import { Field, FieldProps } from 'formik';
import Select from 'react-select';
import { RequiredFieldMarker } from './RequiredFieldMarker';
import { IValueAndLabel } from '../../customTypings/common/ValueAndLabel';
import { ReactSelectBootstrapStyle } from '../../styles/ReactSelectBootstrapStyle';

interface FormFieldProps {
  name: string;
  label?: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  as?: React.ComponentType<FormControlProps>;
  rows?: number;
  options?: Array<{ value: string; label: string }>;
  className?: string;
  helpText?: string;
  isMulti?: boolean;
  closeMenuOnSelect?: boolean;
  showColorPicker?: boolean;
  currentUser?: any;
  showAppointmentType?: boolean;
  showUserSelection?: boolean;
  allAppointmentTypes?: IValueAndLabel[];
  allIdentities?: IValueAndLabel[];
  identityIds?: string[];
  eventToBeUpdated?: any;
  setFieldValue?: (field: string, value: any) => void;
}

export const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  type = 'text',
  placeholder,
  required = false,
  disabled = false,
  as,
  rows,
  options,
  className,
  helpText,
  isMulti = false,
  closeMenuOnSelect = false,
  showColorPicker = false,
  currentUser,
  showAppointmentType = false,
  showUserSelection = false,
  allAppointmentTypes,
  allIdentities,
  identityIds,
  eventToBeUpdated,
  setFieldValue,
}) => {
  const renderField = ({ field, form: { touched, errors } }: FieldProps) => {
    const isInvalid = touched[field.name] && errors[field.name];
    
    // Special handling for calendar-specific fields
    if (showAppointmentType && allAppointmentTypes && setFieldValue) {
      return (
        <Select
          styles={ReactSelectBootstrapStyle}
          className="stateManagedSelect z-2 mb-2"
          onChange={(value) => {
            setFieldValue(
              "appointmentTypeId",
              value == null ? "" : value.value
            );
          }}
          defaultValue={allAppointmentTypes.find(
            (obj) => eventToBeUpdated?.appointmentTypeId === obj.value
          )}
          isClearable
          options={allAppointmentTypes}
        />
      );
    }

    if (showUserSelection && allIdentities && setFieldValue) {
      return (
        <Select
          isMulti
          closeMenuOnSelect={closeMenuOnSelect}
          placeholder="Select users..."
          styles={ReactSelectBootstrapStyle}
          className="stateManagedSelect mb-2"
          onChange={(value) => {
            const identityIds: string[] = [];
            for (let i = 0; i < value.length; i++) {
              identityIds.push(value[i].value);
            }
            setFieldValue("selectedIdentityIds", identityIds);
          }}
          defaultValue={allIdentities.filter((obj) =>
            identityIds?.includes(obj.value)
          )}
          isClearable
          options={allIdentities}
        />
      );
    }

    if (type === 'select' && options) {
      return (
        <Form.Select
          {...field}
          isInvalid={!!isInvalid}
          disabled={disabled}
          className={className}
        >
          <option value="">{placeholder || 'Select an option'}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      );
    }

    if (type === 'textarea') {
      return (
        <Form.Control
          {...field}
          as="textarea"
          rows={rows || 3}
          isInvalid={!!isInvalid}
          disabled={disabled}
          className={className}
          placeholder={placeholder}
        />
      );
    }

    if (type === 'color' && showColorPicker && currentUser?.identity.roles.some(
      (role: any) => role.name === 'SystemAdmin'
    )) {
      return (
        <Form.Control
          {...field}
          type="color"
          isInvalid={!!isInvalid}
          disabled={disabled}
          className={className}
        />
      );
    }

    return (
      <Form.Control
        {...field}
        type={type}
        isInvalid={!!isInvalid}
        disabled={disabled}
        className={className}
        placeholder={placeholder}
        as={as}
      />
    );
  };

  return (
    <Form.Group className="mb-3">
      {label && (
        <Form.Label>
          {label}
          {required && <RequiredFieldMarker />}
        </Form.Label>
      )}
      <Field name={name}>
        {renderField}
      </Field>
      {helpText && <Form.Text className="text-muted">{helpText}</Form.Text>}
      <Field name={name}>
        {({ form: { touched, errors } }: FieldProps) =>
          touched[name] && errors[name] ? (
            <div className="invalid-feedback d-block">{errors[name] as string}</div>
          ) : null
        }
      </Field>
    </Form.Group>
  );
}; 
